import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { StudioMessage } from '@/mocks/adminStudio';
import { studioMessages, typeIcons, typeColors, autoLabelConfig } from '@/mocks/adminStudio';
import { SwalAlert } from '@/hooks/useSweetAlert';

function formatChatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' });
}

function formatChatLabel(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return 'Hoy';
  return d.toLocaleDateString('es-CR', { day: 'numeric', month: 'short' });
}

export default function ConversationPanel() {
  const [messages, setMessages] = useState<StudioMessage[]>(studioMessages.filter((m) => m.status !== 'bloqueado'));
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = useCallback(() => {
    if (!replyText.trim()) return;
    setSending(true);
    setTimeout(() => {
      setSending(false);
      setReplyText('');
      textareaRef.current?.focus();
    }, 300);
  }, [replyText]);

  /* Group by date for labels */
  const groupedMessages = useMemo(() => {
    const groups: { label: string; items: StudioMessage[] }[] = [];
    let currentLabel = '';
    messages.slice().reverse().forEach((m) => {
      const lbl = formatChatLabel(m.timestamp);
      if (lbl !== currentLabel) {
        currentLabel = lbl;
        groups.unshift({ label: lbl, items: [] });
      }
      groups[groups.length - 1].items.push(m);
    });
    return groups;
  }, [messages]);

  return (
    <div className="h-full flex flex-col bg-background-50">
      {/* Header */}
      <div className="shrink-0 px-4 py-3 border-b border-background-200 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
            <i className="ri-chat-3-line text-background-50 text-sm"></i>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-foreground-950">Conversación en vivo</h3>
              <span className="flex items-center gap-1 text-[9px] font-semibold text-emerald-600">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                EN VIVO
              </span>
            </div>
            <p className="text-[10px] text-foreground-400">{messages.length} mensajes · Tardes de Pop con Natalia</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => SwalAlert.toast('Filtros próximamente', 'info')}
            className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors" title="Filtrar">
            <i className="ri-filter-3-line text-foreground-500 text-xs"></i>
          </button>
          <button
            onClick={() => {
              chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors" title="Auto-scroll">
            <i className="ri-arrow-down-double-line text-foreground-500 text-xs"></i>
          </button>
        </div>
      </div>

      {/* Messages feed */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {groupedMessages.map((group) => (
          <div key={group.label}>
            {/* Date label */}
            <div className="flex items-center gap-3 mb-3">
              <div className="flex-1 h-px bg-background-200"></div>
              <span className="text-[9px] font-semibold text-foreground-400 uppercase tracking-wider whitespace-nowrap">{group.label}</span>
              <div className="flex-1 h-px bg-background-200"></div>
            </div>
            <div className="space-y-2">
              {group.items.map((msg) => (
                <ChatBubble key={msg.id} message={msg} />
              ))}
            </div>
          </div>
        ))}
        <div ref={chatEndRef}></div>
      </div>

      {/* Input area */}
      <div className="shrink-0 px-4 py-3 border-t border-background-200 bg-background-50">
        <div className="flex items-end gap-2">
          <button onClick={() => SwalAlert.toast('Adjuntar archivos próximamente', 'info')} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors shrink-0" title="Adjuntar">
            <i className="ri-add-line text-foreground-500 text-sm"></i>
          </button>
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Escribe un mensaje al aire..."
              rows={1}
              maxLength={300}
              className="w-full px-3 py-2 pr-10 rounded-xl bg-background-100 text-foreground-800 text-xs border border-background-200 focus:border-primary-300 focus:ring-1 focus:ring-primary-200 outline-none resize-none placeholder:text-foreground-400 min-h-[36px] max-h-[80px]"
            />
            <span className="absolute right-2.5 bottom-2 text-[8px] text-foreground-400">{replyText.length}/300</span>
          </div>
          <button
            onClick={handleSend}
            disabled={!replyText.trim() || sending}
            className="w-9 h-9 rounded-xl bg-primary-500 text-background-50 flex items-center justify-center cursor-pointer hover:bg-primary-600 transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <i className={`${sending ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-fill'} text-sm`}></i>
          </button>
        </div>
      </div>
    </div>
  );
}

function ChatBubble({ message }: { message: StudioMessage }) {
  const labelStyle = autoLabelConfig[message.autoLabel] || autoLabelConfig.general;

  return (
    <div className="flex items-start gap-2.5 group">
      <div className="relative shrink-0">
        <div className="w-7 h-7 rounded-full bg-background-200 overflow-hidden">
          <img src={message.senderAvatar} alt={message.senderName} className="w-full h-full object-cover" loading="lazy" />
        </div>
        <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-background-50 flex items-center justify-center">
          <i className={`${typeIcons[message.type]} ${typeColors[message.type]} text-[8px]`}></i>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-semibold text-foreground-800">{message.senderName}</span>
          <span className={`inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[8px] font-semibold ${labelStyle.color}`}>
            <i className={`${labelStyle.icon} text-[7px]`}></i>
            {labelStyle.label}
          </span>
          <span className="text-[9px] text-foreground-400 ml-auto shrink-0">{formatChatTime(message.timestamp)}</span>
        </div>
        <p className="text-[11px] text-foreground-600 mt-0.5 leading-relaxed">{message.content}</p>
        {message.songRequest && (
          <div className="flex items-center gap-1.5 mt-1.5 px-2.5 py-1.5 rounded-lg bg-sky-50 border border-sky-100">
            <i className="ri-music-fill text-sky-500 text-[10px]"></i>
            <span className="text-[10px] font-semibold text-sky-700 truncate">{message.songRequest}</span>
          </div>
        )}
        {message.contestAnswer && (
          <div className="flex items-center gap-1.5 mt-1.5 px-2.5 py-1.5 rounded-lg bg-amber-50 border border-amber-100">
            <i className="ri-trophy-fill text-amber-500 text-[10px]"></i>
            <span className="text-[10px] font-semibold text-amber-700 truncate">{message.contestAnswer}</span>
          </div>
        )}
        {/* Hover actions */}
        <div className="flex items-center gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={() => SwalAlert.toast('Mensaje destacado', 'success')} className="w-5 h-5 rounded-md bg-background-100 flex items-center justify-center cursor-pointer hover:bg-amber-100 hover:text-amber-500 transition-colors" title="Destacar">
            <i className="ri-star-line text-foreground-400 text-[9px]"></i>
          </button>
          <button onClick={() => SwalAlert.toast('Responder próximamente', 'info')} className="w-5 h-5 rounded-md bg-background-100 flex items-center justify-center cursor-pointer hover:bg-primary-100 hover:text-primary-500 transition-colors" title="Responder">
            <i className="ri-reply-line text-foreground-400 text-[9px]"></i>
          </button>
          <button onClick={() => SwalAlert.toast('Mensaje ocultado', 'info')} className="w-5 h-5 rounded-md bg-background-100 flex items-center justify-center cursor-pointer hover:bg-red-100 hover:text-red-500 transition-colors" title="Ocultar">
            <i className="ri-eye-off-line text-foreground-400 text-[9px]"></i>
          </button>
        </div>
      </div>
    </div>
  );
}